"use client";

import * as React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface PillTogglesOption<T extends string> {
  value: T;
  label: React.ReactNode;
  count?: number;
}

export interface PillTogglesProps<T extends string> {
  value: T[];
  onChange: (value: T[]) => void;
  options: PillTogglesOption<T>[];
  className?: string;
}

/**
 * PillToggles — multi-select sibling of PillTabs. Same visual language; each
 * pill toggles independently. An empty selection is meaningful (= no filter).
 */
export function PillToggles<T extends string>({
  value,
  onChange,
  options,
  className,
}: PillTogglesProps<T>) {
  const toggle = (v: T) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };

  return (
    <div className={cn("flex items-center gap-2 overflow-x-auto", className)}>
      {options.map((opt) => {
        const isActive = value.includes(opt.value);
        return (
          <Button
            key={opt.value}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => toggle(opt.value)}
            className={cn(
              isActive &&
                "bg-[var(--flozy-blue-50)] text-primary border-primary hover:bg-[var(--flozy-blue-100)] hover:text-primary",
            )}
          >
            {opt.label}
            {opt.count !== undefined && opt.count > 0 && (
              <span
                className={cn(
                  "ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {opt.count}
              </span>
            )}
          </Button>
        );
      })}
    </div>
  );
}
